<!-- 휴일 달력 (월 단위 CSS Grid) -->
<template>
    <div class="hc" tabindex="0" @keydown="onKeydown">
        <div class="hc__toolbar">
            <button type="button" class="hc__nav" :aria-label="$t('이전 해')" @click="moveYear(-1)">«</button>
            <button type="button" class="hc__nav" :aria-label="$t('이전 달')" @click="move(-1)">‹</button>
            <div class="hc__picker" ref="picker">
                <button
                    type="button"
                    class="hc__title"
                    :aria-expanded="panelOpen ? 'true' : 'false'"
                    @click="togglePanel"
                >
                    {{ year }}{{ $t("년") }} {{ month }}{{ $t("월") }} <span class="hc__caret">▾</span>
                </button>
                <div v-if="panelOpen" class="hc__panel">
                    <div class="hc__panel-year">
                        <button type="button" class="hc__nav" @click="movePanelYear(-1)">‹</button>
                        <span>{{ panelYear }}{{ $t("년") }}</span>
                        <button type="button" class="hc__nav" @click="movePanelYear(1)">›</button>
                    </div>
                    <div class="hc__panel-months">
                        <button
                            v-for="m in 12"
                            :key="m"
                            type="button"
                            class="hc__panel-month"
                            :class="{ 'is-current': panelYear === year && m === month }"
                            @click="selectMonth(m)"
                        >
                            {{ m }}{{ $t("월") }}
                            <i v-if="monthMarks[m]" class="hc__dot" :title="$t('등록된 휴일 있음')"></i>
                        </button>
                    </div>
                </div>
            </div>
            <button type="button" class="hc__nav" :aria-label="$t('다음 달')" @click="move(1)">›</button>
            <button type="button" class="hc__nav" :aria-label="$t('다음 해')" @click="moveYear(1)">»</button>
            <button type="button" class="hc__today-btn" @click="goToday">{{ $t("오늘") }}</button>
            <button type="button" class="hc__today-btn hc__list-btn" @click="openDrawer">
                {{ $t("연간 목록") }}
            </button>
        </div>
        <div class="hc__grid" :aria-busy="loading ? 'true' : 'false'" :style="{ gridTemplateRows: gridRows }">
            <div
                v-for="(w, i) in weekdays"
                :key="'w' + i"
                class="hc__weekday"
                :class="{ 'hc__weekday--sun': i === 0, 'hc__weekday--sat': i === 6 }"
            >
                {{ w }}
            </div>
            <div
                v-for="cell in cells"
                :key="cell.date"
                class="hc__day"
                :class="{
                    'hc__day--out': !cell.inMonth,
                    'hc__day--sat': cell.dow === 6,
                    'hc__day--red': cell.red,
                    'hc__day--today': cell.today,
                }"
                @click="$emit('date-click', cell.date)"
            >
                <button
                    type="button"
                    class="hc__date"
                    :class="{ 'is-sat': cell.dow === 6, 'is-red': cell.red, 'is-today': cell.today }"
                    :aria-label="cell.date + ' ' + $t('휴일 등록')"
                >
                    {{ cell.day }}
                </button>
                <div class="hc__events">
                    <template v-for="h in cell.events.slice(0, maxEvents)">
                        <!-- 법정 공휴일: 수정 불가 (클릭 시 아무 동작 없음) -->
                        <span
                            v-if="h.holiTypeCode === publicCode"
                            :key="h.holiIdx"
                            class="hc__event hc__event--public"
                            :title="h.holiName"
                            @click.stop
                        >
                            {{ h.holiName }}
                        </span>
                        <button
                            v-else
                            :key="h.holiIdx"
                            type="button"
                            class="hc__event hc__event--etc"
                            :title="h.holiName"
                            @click.stop="$emit('event-click', h)"
                        >
                            {{ h.holiName }}
                        </button>
                    </template>
                    <span v-if="cell.events.length > maxEvents" class="hc__more">
                        +{{ cell.events.length - maxEvents }}
                    </span>
                </div>
            </div>
        </div>

        <!-- 연간 휴일 목록 (달력 위에 겹쳐 뜨는 드로어) -->
        <transition name="hc-fade">
            <div v-if="drawerOpen" class="hc__dim" @click="closeDrawer"></div>
        </transition>
        <transition name="hc-slide">
            <aside v-if="drawerOpen" class="hc__drawer" ref="drawer" tabindex="-1">
                <div class="hc__drawer-head">
                    <button type="button" class="hc__nav" @click="moveDrawerYear(-1)">‹</button>
                    <div class="hc__drawer-title">
                        {{ drawerYear }}{{ $t("년") }} {{ $t("휴일") }}
                        <small>({{ drawerList.length }}{{ $t("건") }})</small>
                    </div>
                    <button type="button" class="hc__nav" @click="moveDrawerYear(1)">›</button>
                    <button type="button" class="hc__nav hc__drawer-close" :aria-label="$t('닫기')" @click="closeDrawer">
                        ×
                    </button>
                </div>
                <div class="hc__drawer-body" ref="drawerBody">
                    <p v-if="drawerGroups.length === 0" class="hc__drawer-empty">{{ $t("등록된 휴일이 없습니다.") }}</p>
                    <section
                        v-for="g in drawerGroups"
                        :key="g.month"
                        :ref="'dm' + g.month"
                        class="hc__drawer-month"
                        :class="{ 'is-current': drawerYear === year && g.month === month }"
                    >
                        <h4>{{ g.month }}{{ $t("월") }}</h4>
                        <button
                            v-for="h in g.items"
                            :key="h.holiIdx"
                            type="button"
                            class="hc__drawer-item"
                            @click="onDrawerItem(h)"
                        >
                            <span class="hc__drawer-date">{{ h.dayLabel }}</span>
                            <span class="hc__drawer-name" :title="h.holiName">{{ h.holiName }}</span>
                            <span
                                class="hc__badge"
                                :class="h.holiTypeCode === publicCode ? 'hc__badge--public' : 'hc__badge--etc'"
                            >
                                {{ h.holiTypeCode === publicCode ? $t("법정") : $t("기타") }}
                            </span>
                        </button>
                    </section>
                </div>
            </aside>
        </transition>
    </div>
</template>

<script>
import dayjs from "dayjs";

export default {
    name: "HolidayCalendar",
    props: {
        year: { type: Number, required: true },
        month: { type: Number, required: true }, // 1~12
        holidays: { type: Array, default: () => [] },
        holidaysByYear: { type: Object, default: () => ({}) }, // 연/월 선택 패널의 휴일 표시용
        publicCode: { type: String, default: "A001" },
        loading: { type: Boolean, default: false },
        maxEvents: { type: Number, default: 2 },
    },
    data() {
        return {
            weekdays: ["일", "월", "화", "수", "목", "금", "토"],
            panelOpen: false,
            panelYear: this.year,
            drawerOpen: false,
            drawerYear: this.year,
        };
    },
    beforeDestroy() {
        document.removeEventListener("mousedown", this.onOutsideClick);
    },
    computed: {
        firstDay() {
            return dayjs(new Date(this.year, this.month - 1, 1));
        },
        weeks() {
            return Math.ceil((this.firstDay.day() + this.firstDay.daysInMonth()) / 7);
        },
        gridRows() {
            return `36px repeat(${this.weeks}, minmax(0, 1fr))`;
        },
        // 패널에 표시 중인 연도에서 휴일이 등록된 월 { [1~12]: true }
        monthMarks() {
            const marks = {};
            (this.holidaysByYear[this.panelYear] || []).forEach((h) => {
                const m = parseInt(String(h.baseDate || "").substring(5, 7), 10);
                if (m) marks[m] = true;
            });
            return marks;
        },
        // 드로어: 선택 연도 휴일을 날짜순으로 정렬
        drawerList() {
            return (this.holidaysByYear[this.drawerYear] || [])
                .filter((h) => h.baseDate)
                .map((h) => {
                    const d = dayjs(String(h.baseDate).substring(0, 10));
                    return {
                        ...h,
                        _date: d,
                        dayLabel: `${d.date()}(${this.weekdays[d.day()]})`,
                    };
                })
                .sort((a, b) => a._date.valueOf() - b._date.valueOf());
        },
        drawerGroups() {
            const groups = [];
            this.drawerList.forEach((h) => {
                const m = h._date.month() + 1;
                let g = groups[groups.length - 1];
                if (!g || g.month !== m) groups.push((g = { month: m, items: [] }));
                g.items.push(h);
            });
            return groups;
        },
        eventMap() {
            const map = {};
            (this.holidays || []).forEach((h) => {
                const key = String(h.baseDate || "").substring(0, 10);
                (map[key] = map[key] || []).push(h);
            });
            return map;
        },
        cells() {
            const start = this.firstDay.subtract(this.firstDay.day(), "day");
            const todayStr = dayjs().format("YYYY-MM-DD");
            const list = [];
            for (let i = 0; i < this.weeks * 7; i++) {
                const d = start.add(i, "day");
                const date = d.format("YYYY-MM-DD");
                const events = this.eventMap[date] || [];
                const dow = d.day();
                list.push({
                    date,
                    day: d.date(),
                    dow,
                    inMonth: d.month() === this.month - 1,
                    today: date === todayStr,
                    events,
                    // 일요일 또는 법정 공휴일
                    red: dow === 0 || events.some((h) => h.holiTypeCode === this.publicCode),
                });
            }
            return list;
        },
    },
    methods: {
        move(diff) {
            const d = this.firstDay.add(diff, "month");
            this.$emit("change", { year: d.year(), month: d.month() + 1 });
        },
        moveYear(diff) {
            const d = this.firstDay.add(diff, "year");
            this.$emit("change", { year: d.year(), month: d.month() + 1 });
        },
        togglePanel() {
            if (this.panelOpen) return this.closePanel();
            this.panelYear = this.year;
            this.panelOpen = true;
            this.$emit("year-preview", this.panelYear);
            document.addEventListener("mousedown", this.onOutsideClick);
        },
        closePanel() {
            this.panelOpen = false;
            document.removeEventListener("mousedown", this.onOutsideClick);
        },
        onOutsideClick(e) {
            if (this.$refs.picker && !this.$refs.picker.contains(e.target)) this.closePanel();
        },
        movePanelYear(diff) {
            this.panelYear += diff;
            this.$emit("year-preview", this.panelYear);
        },
        selectMonth(m) {
            this.$emit("change", { year: this.panelYear, month: m });
            this.closePanel();
        },
        openDrawer() {
            this.closePanel();
            this.drawerYear = this.year;
            this.drawerOpen = true;
            this.$emit("year-preview", this.drawerYear);
            this.$nextTick(() => {
                this.$refs.drawer && this.$refs.drawer.focus();
                this.scrollDrawerToCurrent();
            });
        },
        closeDrawer() {
            this.drawerOpen = false;
            this.$nextTick(() => this.$el && this.$el.focus());
        },
        moveDrawerYear(diff) {
            this.drawerYear += diff;
            this.$emit("year-preview", this.drawerYear);
            this.$nextTick(this.scrollDrawerToCurrent);
        },
        // 조회 중인 달(없으면 맨 위)로 스크롤
        scrollDrawerToCurrent() {
            const body = this.$refs.drawerBody;
            if (!body) return;
            const el = this.drawerYear === this.year && this.$refs["dm" + this.month];
            const target = Array.isArray(el) ? el[0] : el;
            body.scrollTop = target ? target.offsetTop - body.offsetTop : 0;
        },
        // 항목 클릭: 해당 달로 이동, 기타 휴일이면 수정 모달까지 연다
        onDrawerItem(h) {
            this.$emit("change", { year: h._date.year(), month: h._date.month() + 1 });
            this.closeDrawer();
            if (h.holiTypeCode !== this.publicCode) {
                const row = (this.holidaysByYear[this.drawerYear] || []).find((r) => r.holiIdx === h.holiIdx);
                this.$emit("event-click", row || h);
            }
        },
        // ←/→: 월 이동, PageUp/PageDown: 연 이동, ESC: 패널 닫기
        onKeydown(e) {
            if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
            if (this.drawerOpen) {
                if (e.key === "Escape") {
                    e.preventDefault();
                    this.closeDrawer();
                }
                return;
            }
            const keys = {
                ArrowLeft: () => this.move(-1),
                ArrowRight: () => this.move(1),
                PageUp: () => this.moveYear(-1),
                PageDown: () => this.moveYear(1),
                Escape: () => this.closePanel(),
            };
            if (keys[e.key]) {
                e.preventDefault();
                keys[e.key]();
            }
        },
        goToday() {
            const d = dayjs();
            this.$emit("change", { year: d.year(), month: d.month() + 1 });
        },
    },
};
</script>

<style scoped>
.hc {
    position: relative;
    --hc-border: #e6eaf2;
    --hc-head-bg: #f8f9fc;
    --hc-sat-bg: #f4f7fb;
    --hc-sat-text: #1f5f8b;
    --hc-red-bg: #fff5f5;
    --hc-red-text: #d32f2f;
    --hc-out-bg: #f4f6fa;
    --hc-hover-bg: #eef3fa;
    --hc-sat-hover-bg: #e6edf6;
    --hc-red-hover-bg: #ffe8e8;
    --hc-out-hover-bg: #e8ecf2;
    --hc-today: #1e3a5f;
    --hc-etc-bg: #eaf3ff;
    --hc-etc-text: #1f5f8b;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
}
.hc__toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 0 10px;
}
.hc:focus {
    outline: none;
}
.hc__picker {
    position: relative;
}
.hc__title {
    min-width: 140px;
    height: 32px;
    padding: 0 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    cursor: pointer;
}
.hc__title:hover {
    background: var(--hc-head-bg);
}
.hc__caret {
    font-size: 12px;
    color: #6b7280;
}
.hc__panel {
    position: absolute;
    top: 38px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 20;
    width: 260px;
    padding: 10px;
    background: #fff;
    border: 1px solid var(--hc-border);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
}
.hc__panel-year {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-weight: 700;
    color: #1f2937;
}
.hc__panel-months {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}
.hc__panel-month {
    position: relative;
    height: 36px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
    color: #374151;
    cursor: pointer;
}
.hc__panel-month:hover {
    background: var(--hc-hover-bg);
}
.hc__panel-month.is-current {
    background: var(--hc-today);
    color: #fff;
}
.hc__dot {
    position: absolute;
    top: 5px;
    right: 7px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--hc-red-text);
}
.hc__panel-month.is-current .hc__dot {
    background: #fff;
}
.hc__nav,
.hc__today-btn {
    border: 1px solid var(--hc-border);
    background: #fff;
    border-radius: 8px;
    height: 32px;
    padding: 0 12px;
    color: #374151;
    cursor: pointer;
}
.hc__nav {
    width: 32px;
    padding: 0;
    font-size: 18px;
    line-height: 1;
}
.hc__nav:hover,
.hc__today-btn:hover {
    background: var(--hc-head-bg);
}
.hc__today-btn {
    margin-left: 4px;
    font-size: 13px;
}
.hc__grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    border: 1px solid var(--hc-border);
    border-radius: 14px;
    overflow: hidden;
    background: #fff;
}
.hc__grid[aria-busy="true"] {
    opacity: 0.6;
    pointer-events: none;
}
.hc__weekday {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--hc-head-bg);
    border-bottom: 1px solid var(--hc-border);
    border-right: 1px solid var(--hc-border);
    font-size: 12px;
    color: #4b5563;
}
.hc__weekday--sun {
    color: var(--hc-red-text);
}
.hc__weekday--sat {
    color: var(--hc-sat-text);
}
.hc__day {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    min-height: 0;
    padding: 8px 10px;
    overflow: hidden;
    border-right: 1px solid var(--hc-border);
    border-bottom: 1px solid var(--hc-border);
    cursor: pointer;
}
.hc__day:nth-child(7n) {
    border-right: 0;
}
.hc__day--sat {
    background: var(--hc-sat-bg);
}
.hc__day--red {
    background: var(--hc-red-bg);
}
.hc__day--out {
    background: var(--hc-out-bg);
    opacity: 0.55;
}
.hc__day:hover {
    background: var(--hc-hover-bg);
}
.hc__day--sat:hover {
    background: var(--hc-sat-hover-bg);
}
.hc__day--red:hover {
    background: var(--hc-red-hover-bg);
}
.hc__day--out:hover {
    background: var(--hc-out-hover-bg);
}
.hc__date {
    align-self: flex-start;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    cursor: pointer;
}
.hc__date:hover {
    background: rgba(30, 58, 95, 0.1);
}
.hc__date.is-sat {
    color: var(--hc-sat-text);
}
.hc__date.is-red {
    color: var(--hc-red-text);
}
.hc__date.is-today,
.hc__date.is-today:hover {
    background: var(--hc-today);
    color: #fff;
}
.hc__events {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 0;
}
.hc__event {
    display: block;
    width: 100%;
    padding: 1px 6px;
    border: 0;
    border-radius: 4px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.hc__event--public {
    background: #ffe9e9;
    color: #b71c1c;
    cursor: default;
}
.hc__event--etc {
    cursor: pointer;
    background: var(--hc-etc-bg);
    color: var(--hc-etc-text);
}
.hc__more {
    font-size: 11px;
    color: #6b7280;
    padding-left: 6px;
}
.hc__list-btn {
    margin-left: auto;
}
.hc__dim {
    position: absolute;
    inset: 0;
    z-index: 30;
    background: rgba(15, 23, 42, 0.28);
    border-radius: 14px;
}
.hc__drawer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 31;
    display: flex;
    flex-direction: column;
    width: 340px;
    max-width: 90%;
    background: #fff;
    border-left: 1px solid var(--hc-border);
    border-radius: 14px 0 0 14px;
    box-shadow: -8px 0 24px rgba(15, 23, 42, 0.16);
    outline: none;
}
.hc__drawer-head {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px;
    border-bottom: 1px solid var(--hc-border);
}
.hc__drawer-title {
    flex: 1;
    text-align: center;
    font-weight: 700;
    color: #1f2937;
}
.hc__drawer-title small {
    font-weight: 400;
    color: #6b7280;
}
.hc__drawer-close {
    margin-left: 4px;
}
.hc__drawer-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 4px 12px 16px;
}
.hc__drawer-empty {
    margin: 32px 0;
    text-align: center;
    color: #9ca3af;
}
.hc__drawer-month h4 {
    position: sticky;
    top: 0;
    margin: 0;
    padding: 12px 0 6px;
    background: #fff;
    font-size: 13px;
    font-weight: 700;
    color: #6b7280;
}
.hc__drawer-month.is-current h4 {
    color: var(--hc-today);
}
.hc__drawer-month.is-current h4::after {
    content: "●";
    margin-left: 6px;
    font-size: 8px;
    vertical-align: middle;
}
.hc__drawer-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    text-align: left;
    font-size: 13px;
    cursor: pointer;
}
.hc__drawer-item:hover {
    background: var(--hc-hover-bg);
}
.hc__drawer-date {
    flex: 0 0 60px;
    color: #374151;
    font-variant-numeric: tabular-nums;
}
.hc__drawer-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #111827;
}
.hc__badge {
    flex: 0 0 auto;
    padding: 1px 7px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
}
.hc__badge--public {
    background: #ffe9e9;
    color: #b71c1c;
}
.hc__badge--etc {
    background: var(--hc-etc-bg);
    color: var(--hc-etc-text);
}
.hc-slide-enter-active,
.hc-slide-leave-active,
.hc-fade-enter-active,
.hc-fade-leave-active {
    transition: all 0.2s ease;
}
.hc-slide-enter,
.hc-slide-leave-to {
    transform: translateX(100%);
}
.hc-fade-enter,
.hc-fade-leave-to {
    opacity: 0;
}
</style>
